import { formatXof } from '@washed/i18n';

export type PlanStatus = 'live' | 'soon';
export type PlanSegment = 'solo' | 'duo' | 'foyer';

export interface PlanRow {
  code: 'S1' | 'S2' | 'D1' | 'D2' | 'F1' | 'F2';
  segment: PlanSegment;
  visitsPerMonth: 1 | 2;
  residents: string;
  price: string;
  amountXof: number;
  recommended: boolean;
  status: PlanStatus;
}

/**
 * Authoritative public paid-beta pricing for Lomé.
 * Customer-facing copy uses F CFA; formatXof is retained only as a stable
 * zero-decimal formatter for the numeric amount.
 */
export const SOLO_PLANS: PlanRow[] = [
  {
    code: 'S1',
    segment: 'solo',
    visitsPerMonth: 1,
    residents: '1',
    price: formatXof(4500n),
    amountXof: 4500,
    recommended: false,
    status: 'live',
  },
  {
    code: 'S2',
    segment: 'solo',
    visitsPerMonth: 2,
    residents: '1',
    price: formatXof(8500n),
    amountXof: 8500,
    recommended: true,
    status: 'live',
  },
];

export const DUO_PLANS: PlanRow[] = [
  {
    code: 'D1',
    segment: 'duo',
    visitsPerMonth: 1,
    residents: '2',
    price: formatXof(6000n),
    amountXof: 6000,
    recommended: false,
    status: 'live',
  },
  {
    code: 'D2',
    segment: 'duo',
    visitsPerMonth: 2,
    residents: '2',
    price: formatXof(11500n),
    amountXof: 11500,
    recommended: true,
    status: 'live',
  },
];

export const FOYER_PLANS: PlanRow[] = [
  {
    code: 'F1',
    segment: 'foyer',
    visitsPerMonth: 1,
    residents: '3–5',
    price: formatXof(9500n),
    amountXof: 9500,
    recommended: false,
    status: 'live',
  },
  {
    code: 'F2',
    segment: 'foyer',
    visitsPerMonth: 2,
    residents: '3–5',
    price: formatXof(18000n),
    amountXof: 18000,
    recommended: true,
    status: 'live',
  },
];

export const ALL_PLANS: PlanRow[] = [...SOLO_PLANS, ...DUO_PLANS, ...FOYER_PLANS];

export interface PlanUnitLine {
  /** Solo 2× is one person, so its line is per wash; Duo and Foyer are per person per wash. */
  kind: 'per_wash' | 'per_person_per_wash';
  /** Lowest and highest price per unit; equal when the card covers one household size. */
  min: number;
  max: number;
}

/**
 * Whole francs, rounded up so a displayed share is never below what a person pays. Integer
 * maths like core-domain (money never goes through floating point, even for display).
 */
const ceilDiv = (amount: number, by: number) =>
  Number((BigInt(amount) + BigInt(by) - 1n) / BigInt(by));

/** The household sizes a card advertises (Foyer: 3 to 5; 6 and 7 are paid extra seats). */
const ADVERTISED_SIZES: Record<PlanSegment, readonly number[]> = {
  solo: [1],
  duo: [2],
  foyer: [3, 4, 5],
};

/**
 * The value line of a pricing card: what one wash costs each person, the unit the WhatsApp
 * bot sells by (washed-wa-operator #70, founder 2026-10-25), so every card reads against
 * Solo 1×, one wash alone. Month ÷ people ÷ visits, rounded up; Foyer shows its whole range
 * (core-domain planUnitPrices: a range, never the best case alone). Nothing on Solo 1×, the
 * reference. plans.test.ts pins the figures to @washed/core-domain.
 */
export function planUnitLine(plan: PlanRow): PlanUnitLine | null {
  if (plan.segment === 'solo' && plan.visitsPerMonth === 1) return null;
  const shares = ADVERTISED_SIZES[plan.segment].map((people) =>
    ceilDiv(plan.amountXof, people * plan.visitsPerMonth),
  );
  return {
    kind: plan.segment === 'solo' ? 'per_wash' : 'per_person_per_wash',
    min: Math.min(...shares),
    max: Math.max(...shares),
  };
}

/**
 * What each person pays for their one wash when a full Foyer 2× splits the two visits (part
 * of the group at the 1st, the rest at the 2nd): the month ÷ 5, never the 1 800 F of a group
 * washed at both visits. Null on every other plan.
 */
export function splitVisitShare(plan: PlanRow): number | null {
  if (plan.segment !== 'foyer' || plan.visitsPerMonth !== 2) return null;
  const people = Math.max(...ADVERTISED_SIZES.foyer);
  return ceilDiv(plan.amountXof, people);
}
